// ABOUTME: Main Progress Center component that displays active background actions in the Topbar.
// ABOUTME: Shows a badge icon when actions exist, and a popover with action details when clicked.

import { Box, Button, useTheme } from "@mui/material";
import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SyncIcon from '@mui/icons-material/Sync';
import { tokens } from "../../../../theme";
import PopoverModal from "../../../theme/Modal/PopoverModal";
import Text from "../../../theme/text/Text";
import {
    selectActions,
    selectPopoverOpen,
    selectHasActions,
    setPopoverOpen,
    removeAction,
} from "../../../../newapi/global/actionProgressSlice";
import { ACTION_TYPES } from "./actionTypes";

const ProgressCenter = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const buttonRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);

    const actions = useSelector(selectActions);
    const popoverOpen = useSelector(selectPopoverOpen);
    const hasActions = useSelector(selectHasActions);

    // Set anchorEl when button mounts and popoverOpen is true (for auto-open)
    useEffect(() => {
        if (buttonRef.current && popoverOpen) {
            setAnchorEl(buttonRef.current);
        } else if (!popoverOpen) {
            setAnchorEl(null);
        }
    }, [popoverOpen, hasActions]);

    const actionList = Object.values(actions);
    const actionCount = actionList.length;

    const handleClick = () => {
        dispatch(setPopoverOpen(!popoverOpen));
    };

    const handleClose = () => {
        dispatch(setPopoverOpen(false));
    };

    const handleRemoveAction = (id) => {
        dispatch(removeAction(id));
    };

    if (!hasActions) {
        return null;
    }

    return (
        <>
            <Button
                ref={buttonRef}
                onClick={handleClick}
                TouchRippleProps={{
                    style: {
                        color: theme.palette.mode === "dark" ? "rgba(255,255,255,.5)" : 'rgba(49, 65, 95, 0.5)'
                    }
                }}
                variant='contained'
                sx={{
                    background: theme.palette.mode === "dark" ? colors.nav["linkBg"] : "#fff",
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    padding: 0,
                    minWidth: 0,
                    boxShadow: "none",
                    display: "flex",
                    alignItems: "center",
                    transition: "250ms",
                    color: colors.txt["primary"],
                    position: 'relative',
                    opacity: 1,
                    "&:hover": {
                        transition: "250ms",
                        background: theme.palette.mode === "dark" ? colors.grey[300] : "white",
                        boxShadow: "0px 2px 8px rgba(49, 65, 95, 0.25)"
                    },
                    "@media screen and (max-width: 47.9375em)": {
                        width: 32,
                        height: 32,
                        borderRadius: 16
                    }
                }}
            >
                <SyncIcon
                    sx={{
                    }}
                />
                {actionCount > 0 &&
                    <Box
                        sx={{
                            position: 'absolute',
                            background: theme.palette.mode === "dark" ? colors.primary[400] : colors.primary[400],
                            color: theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light'],
                            fontWeight: "bold",
                            height: 24,
                            display: 'flex',
                            alignItems: "center",
                            px: 0.5,
                            borderRadius: 3,
                            top: -6,
                            right: -6,
                        }}
                    >
                        {actionCount}
                    </Box>
                }
            </Button>

            <PopoverModal
                id="progress-center-popover"
                open={popoverOpen && !!anchorEl}
                anchorEl={anchorEl}
                onClose={handleClose}
            >
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[100] : "#fff",
                        padding: 1.5,
                        minWidth: 420,
                        maxWidth: 500,
                        borderRadius: "24px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pb: 1,
                        }}
                    >
                        <Text bold fontSize={14}>{t("active_actions")}</Text>
                    </Box>

                    {actionList.map((action) => {
                        const actionType = ACTION_TYPES[action.type];
                        if (!actionType) return null;

                        const ActionComponent = actionType.component;
                        return (
                            <ActionComponent
                                key={action.id}
                                action={action}
                                onRemove={() => handleRemoveAction(action.id)}
                            />
                        );
                    })}
                </Box>
            </PopoverModal>
        </>
    );
};

export default ProgressCenter;
